<template>
	<router-link
		:to="routeTo"
		class="deal-pack-card"
	>
		<div class="deal-pack-card__image">
			<img :src="imageUrl" alt="" />
			<div v-if="teacherMode" class="deal-pack-card__image--status" :class="dealPack.status">
				{{ dealPack.status }}
			</div>
		</div>
		<div class="deal-pack-card__text">
			<p class="deal-pack-card__text--title">
				{{ dealPack.title }}
			</p>
			<p class="deal-pack-card__text--subtitle">
				{{ dealPack.description }}
			</p>
		</div>

		<div class="deal-pack-card__info">
			<div class="deal-pack-card__info--first-row">
				<template v-if="dealPack.biddingSystems && dealPack.biddingSystems.length">
					<tooltip
						v-for="tag in dealPack.biddingSystems"
						:key="tag"
						text="Bidding system for this deal pack"
						position="top"
					>
						<div class="tag">
							{{ tag }}
						</div>
					</tooltip>
				</template>
				<tooltip
					v-else
					text="Bidding system for this deal pack"
					position="top"
				>
					<div class="tag">
						Any
					</div>
				</tooltip>
			</div>
			<div class="deal-pack-card__info--second-row">
				<div class="deal-pack-card__info-wrapper">
					<div class="deal-pack-card__info--block tag">
						{{ getDealsCount }} Deals
					</div>
					<div class="deal-pack-card__info--block tag">
						<icon icon="v2-shield" colour="transparent" />
						{{ getLevel }}
					</div>
				</div>

				<v2-deal-pack-progress class="deal-pack-card__info--progress" v-if="dealPack.activated" :value="dealPack.activityProgress || 0" />

				<div v-else-if="!dealPack.activated" class="deal-pack-card__info--price-tags">
					<tooltip
						v-if="getCost"
						text="Non-subscriber price"
						position="top"
						class="non-subscriber"
					>
						<div v-if="getCost" class="deal-pack-card__info--price" :class="{ free: getIsFree, inactive: getCostIsInactive }">
							<div class="price-block">
								{{ getIsFree ? 'FREE' : getCost }}
							</div>
						</div>
					</tooltip>
					<tooltip
						v-if="getDiscountCost"
						text="Handle subscriber price"
						position="top"
						class="subscriber"
					>
						<div v-if="getDiscountCost" class="deal-pack-card__info--price" :class="{ free: getIsFreeByDiscount, inactive: getDiscountCostIsInactive }">
							<div class="price-block">
								{{ getIsFreeByDiscount ? 'FREE' : getDiscountCost }}
							</div>
							<icon icon="handle-logo" colour="transparent" />
						</div>
					</tooltip>
				</div>

			</div>
		</div>
	</router-link>
</template>

<script>
	import Icon                                   from '@/components/ui/Icon';
	import V2DealPackProgress                     from '@/components/dealPacks/V2DealPackProgress';
	import Tooltip                                from '@/components/ui/Tooltip';
	import routeParams                            from '@/mixins/routeParams';
	import { dealPackCost, dealPackDiscountCost } from '@/formatters';

	export default {
		components: {
			Icon,
			V2DealPackProgress,
			Tooltip
		},
		mixins: [routeParams],
		props: {
			dealPack: {
				type: Object,
				required: true
			},
			teacherMode: {
				type: Boolean,
				default: () => false
			},
			adminMode: {
				type: Boolean,
				default: () => false
			}
		},
		computed: {
			routeTo () {
        if (this.adminMode) {
          return `/admin/deal-packs/${this.dealPack.id}`;
        }
        if (this.teacherMode) {
          return `/teaching/deal-packs/${this.dealPack.id}`;
        }

        if (this.dealPack.activated) {
					return `/my-deals/${this.dealPack.id}`;
				}
				return `/deal-store/${this.dealPack.id}`;
			},

			imageUrl () {
        if (this.dealPack?.media?.[0]?.url) {
          return this.dealPack.media[0].url;
        }

				const focusTypes = {
					bidding: 'Bidding',
					'declarer play': 'Play',
					defence: 'Defence'
				};

				const focus = focusTypes[this.dealPack.focus[0]] || 'Other';

				return `/card-images/${focus}_Lev${this.dealPack.maxExperienceLevel}.png`;
			},
			getDealsCount () {
				return this.dealPack.dealIds?.length || 0;
			},
			getLevel () {
        if (this.dealPack.minExperienceLevel === this.dealPack.maxExperienceLevel) {
          return this.dealPack.minExperienceLevel;
        }

        return `${this.dealPack.minExperienceLevel}-${this.dealPack.maxExperienceLevel}`;
			},
			getIsFreeByDiscount () {
				if (this.dealPack?.costWithDiscount) {
					return parseInt(this.dealPack?.costWithDiscount) === 0;
				}
				return false;
			},
			getIsFree () {
				return this.dealPack?.isFree === true;
			},
			getCost () {
				return dealPackCost(this.dealPack);
			},
			getCostIsInactive () {
				return this.getDiscountCost && this.getIsSubscriber && !this.getIsFree;
			},
			getDiscountCost () {
				return dealPackDiscountCost(this.dealPack);
			},
			getDiscountCostIsInactive () {
				return !this.getIsSubscriber;
			},
			getIsSubscriber () {
				return this.$store.getters['auth/getIsHandleSubscriber'];
			}
		}
	};

</script>

<style lang="scss" scoped>
	.deal-pack-card {
    display: flex;
    flex-direction: column;
    border: 1px solid $c-gray-d2;
    border-radius: 4px;

    &__image {
      width: 100%;
      height: 150px;
      border-radius: 4px 4px 0 0;
      overflow: hidden;
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &--status {
        position: absolute;
        right: 16px;
        top: 16px;
        border-radius: 2px;
        padding: 4px 8px;
        text-transform: uppercase;
        @include font(14px, $c-white-l, 700, 18px);

        &.published {
          background: $c-dark-navy;
        }

        &.draft {
          background: $c-light-navy;
        }
      }
    }

    &__text {
      display: flex;
			flex: 1;
      flex-direction: column;
      padding: 12px;
      justify-content: flex-start;
      width: 100%;
      background: $c-white-l;
      position: relative;
      gap: 6px;

      &--title{
        @include font(20px, $c-dark-navy, 500, 26px);
        @include text-overflow(2);
        width: 100%;
      }

      &--subtitle{
        @include font(16px, $c-medium-navy, 400, 20px);
        @include text-overflow(2);
        width: 100%;
      }
    }

    &__info {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px;
      width: 100%;
      background: $c-gray-l;

      &--first-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        width: fit-content;

        ::v-deep [data-element="tooltip-text-wrapper"] {
          min-height: rem(20);
          min-width: rem(116);
          margin-inline-start: -64px !important;

          [data-element="tooltip-text"] {
            font-size: 12px !important;
            font-weight: 400 !important;
            line-height: 14px !important;
          }
        }
      }

      &--second-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }

      &-wrapper {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
      }

      &--progress {
        width: 100px !important;
        margin-left: auto;
      }

			&--price-tags {
				display: flex;
				gap: 4px;
				margin-left: auto;

				::v-deep [data-component="tooltip"] {
					&.non-subscriber {
						[data-element="tooltip-text-wrapper"] {
							min-width: rem(116);
							margin-inline-start: -64px !important;
						}
					}

					&.subscriber {
						[data-element="tooltip-text-wrapper"] {
							min-width: rem(130);
							margin-inline-start: -70px !important;
						}
					}

					[data-element="tooltip-text-wrapper"] {
						min-height: rem(20);

						[data-element="tooltip-text"] {
							font-size: 12px !important;
							font-weight: 400 !important;
							line-height: 14px !important;
						}
					}
				}
			}

			&--price {
				position: relative;
				display: flex;
				justify-content: center;
				align-items: center;
				padding: 4px 6px;
				background: $c-medium-navy;
				@include font(14px, $c-white-d, 700, 18px);

				&.free {
					color: $c-white-l;
					background: $c-green;
				}

				&.inactive {
					text-decoration: line-through;
					opacity: 0.5;
				}

				[data-component="icon"] {
					position: absolute;
					width: 18px;
					height: 18px;
					top: -8px;
					right: -8px;
				}
			}

			&--block {
				&.tag {
					padding: 4px 6px;
				}
			}
    }
  }

</style>
